import { inject, Injectable } from "@angular/core";
import { Origin, SyncStatusEnum } from "../../models/types/general-types";
import { AccountView } from "../../models/view/account/account.view";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { SyncStatusView } from "@bitwarden/web-vault/app/models/view/sync-status/sync-status.view";
import { ImportStoreService } from "@bitwarden/web-vault/app/services/store/import/import.store.service";
import { GlossSyncFactory } from "@bitwarden/web-vault/app/services/syncing/gloss.sync.factory";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { GlobalService } from "@bitwarden/common/services/global/global.service";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { GlossSyncService } from "@bitwarden/web-vault/app/services/syncing/gloss.sync.service";
import { InstitutionView } from "@bitwarden/web-vault/app/models/view/institution/institution.view";
import { basiqIntegrationEnabled } from "@bitwarden/web-vault/utils/flags";
import { FiskilApi } from "@bitwarden/web-vault/app/services/syncing/fiskil/fiskil.api";
import type { FiskilSyncStatusResponse } from "@bitwarden/web-vault/app/services/syncing/fiskil/types";
import { PluginConfigService } from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-config.service";
import {
  fiskilVaultAccountsMissingImportedTransactions,
  resolveFiskilBootstrapAction,
  shouldRecoverFiskilImport,
} from "@bitwarden/web-vault/app/services/syncing/fiskil/fiskil.recovery";
import { PlaidApi } from "@bitwarden/web-vault/app/services/syncing/plaid/plaid.api";
import {
  type PlaidBootstrapAction,
  plaidVaultAccountsMissingImportedTransactions,
  resolvePlaidBootstrapAction,
} from "@bitwarden/web-vault/app/services/syncing/plaid/plaid.recovery";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { isExcludedFromAutoSync } from "@bitwarden/web-vault/app/services/syncing/sync-account-eligibility";

export type OriginAccountsMap = Map<Origin, AccountView[]>;
export type BasiqImportData = string;
export type FiskilImportData = {
  mode?: "redirect" | "recovery";
  /** Avoid a second sync-status round-trip when bootstrap already fetched status. */
  syncStatus?: FiskilSyncStatusResponse;
};
export type PlaidImportData = {
  mode?: "redirect" | "recovery";
  itemId?: string;
};
export type OriginImportData = BasiqImportData | FiskilImportData | PlaidImportData;

type PlaidRecoveryContext = "bootstrap" | "session";

type PlaidRecoveryEvaluation = {
  action: PlaidBootstrapAction;
  serverItemCount: number;
  vaultPlaidAccounts: AccountView[];
};

@Injectable({
  providedIn: "root",
})
export class GlossSync {
  private userStore: UserStoreService;
  private importStore: ImportStoreService;
  private syncFactory: GlossSyncFactory;
  private glossSyncService: GlossSyncService;
  private confirmationDialog: ConfirmationDialogService;
  private fiskilApi = inject(FiskilApi);
  private plaidApi = inject(PlaidApi);
  private pluginConfigService = inject(PluginConfigService);
  private logService = inject(LogService);
  private transactionStore = inject(TransactionStoreService);
  private globalService = inject(GlobalService);
  private plaidRecoveryInFlight: Promise<boolean> | null = null;

  constructor() {
    this.userStore = inject(UserStoreService);
    this.importStore = inject(ImportStoreService);
    this.syncFactory = inject(GlossSyncFactory);
    this.confirmationDialog = inject(ConfirmationDialogService);
    this.glossSyncService = inject(GlossSyncService);
  }

  /** Connects to the specified institution via connectors such as Basiq, Plaid, etc...*/
  async connectToInstitution(origin: Origin, institution: InstitutionView) {
    await this.syncFactory.connectToInstitution(origin, institution);
  }
  /** Reconnects(consent then connect) to the specified institution via connectors such as Basiq, Plaid, etc...*/
  async reconnectToInstitution(origin: Origin, institution: InstitutionView) {
    await this.syncFactory.reconnectToInstitution(origin, institution);
  }

  async addPlaidAccounts(institution: InstitutionView): Promise<void> {
    await this.syncFactory.addPlaidAccounts(institution);
  }

  /** Connects to a connector such as Basiq, Plaid, etc...*/
  async connect(origin: Origin, isFromGuide: boolean = false) {
    await this.syncFactory.connect(origin, isFromGuide);
  }

  /**
   * @param origin - Basiq?, Plaid?
   * @param originImportData - BasiqImportData(jobIds)?, PlaidImportData(?)
   * Import from Basiq for the first time: At this stage user has come back from Basiq and
   *  has the basiq jobIds  for each institution he/she connected*/
  async import(origin: Origin, originImportData: OriginImportData) {
    this.glossSyncService.setRefreshBlocked(true);
    this.glossSyncService.runningSyncState.set({
      ...this.glossSyncService.runningSyncState(),
      isRunning: true,
      origin: origin,
    });
    try {
      await this.syncFactory.import(origin, originImportData);
    } catch (error) {
      await this.glossSyncService.logError(
        [],
        error instanceof Error ? error : new Error(String(error)),
        "Importing accounts",
        SyncStatusEnum.unknownError,
      );
      this.confirmationDialog.notify("error", "error", "somethingWentWrong");
    } finally {
      this.glossSyncService.setRefreshBlocked(false);
    }
  }

  /** Sync given Accounts. */
  async sync(accounts: AccountView[]) {
    // Nothing eligible (e.g. only org-owned accounts, see isExcludedFromAutoSync): stop before
    // initializeSync's empty saveToVault writes, which still take the vault lock and flash the
    // "Saving data..." spinner for a guaranteed no-op.
    const syncableAccounts = accounts.filter((acc) => !isExcludedFromAutoSync(acc));
    if (!syncableAccounts.length) {
      return;
    }
    this.glossSyncService.setRefreshBlocked(true);
    try {
      const autoAccounts = await this.initializeSync(syncableAccounts);
      const accountOriginGroup = this.originAccountsMap(autoAccounts);

      for (const [origin, accounts] of accountOriginGroup) {
        if (origin === Origin.basiq && !basiqIntegrationEnabled()) {
          continue;
        }
        await this.syncFactory.sync(origin, accounts);
      }
    } catch (error) {
      await this.glossSyncService.terminateSync(syncableAccounts, SyncStatusEnum.unknownError);
      await this.glossSyncService.logError(
        syncableAccounts,
        error,
        "Syncing accounts",
        SyncStatusEnum.unknownError,
      );
      this.confirmationDialog.notify("error", "error", "somethingWentWrong");
    } finally {
      this.glossSyncService.setRefreshBlocked(false);
    }
  }

  /** Returns true if auto-sync on login should run. Only throttles when Basiq accounts exist and were synced within 12 hours. */
  shouldSyncBasiqOnLogin(accounts: AccountView[]): boolean {
    const basiqAccounts = accounts.filter((acc) => acc.origin === Origin.basiq);
    if (basiqAccounts.length === 0) {
      return true;
    }
    const syncStatusViews = this.importStore.syncStatus.syncStatusViews() ?? [];
    const basiqAccountIds = new Set(basiqAccounts.map((a) => a.id));
    const syncedStatuses = syncStatusViews.filter(
      (s) => basiqAccountIds.has(s.accountId) && s.key === SyncStatusEnum.synced,
    );
    if (syncedStatuses.length === 0) {
      return true;
    }
    const lastSyncTime = Math.max(...syncedStatuses.map((s) => s.dateModified.getTime()));
    const twelveHoursMs = 12 * 60 * 60 * 1000;
    return Date.now() - lastSyncTime > twelveHoursMs;
  }

  /** Sync all the syncable accounts */
  async syncAll(userInitiated: boolean = false) {
    try {
      const allAccounts = this.userStore.enrichAccounts.collection();
      const isSyncable = this.isSyncableAccounts(allAccounts);
      if (!isSyncable) {
        if (userInitiated) {
          this.confirmationDialog.notify("info", "noSyncableAccounts", "youHaveNoAutoAccounts");
        }
        return;
      }

      await this.sync(allAccounts);
    } catch (error) {
      this.confirmationDialog.notify("error", "error", "somethingWentWrong");
      this.logService.debug(
        `Syncing All accounts failed: ${error instanceof Error ? error.message : JSON.stringify(error)}`,
      );
    }
  }

  /**
   * Post-login bootstrap: Fiskil recovery/import when needed, then sync other origins.
   * Does not surface import errors as dialogs (logs only).
   */
  async bootstrapAutoSync(): Promise<void> {
    await this.glossSyncService.runWithBootstrapFetchTimeout(async () => {
      const fiskilRecoveryRan = await this.bootstrapFiskilSync();
      const plaidRecoveryRan = await this.bootstrapPlaidSync();

      const allAccounts = this.userStore.enrichAccounts.collection();
      if (!allAccounts?.length) {
        return;
      }

      const accountsToSync = allAccounts.filter(
        (acc) =>
          !isExcludedFromAutoSync(acc) &&
          !(fiskilRecoveryRan && acc.origin === Origin.fiskil) &&
          !(plaidRecoveryRan && acc.origin === Origin.plaid),
      );

      if (accountsToSync.length === 0) {
        return;
      }

      try {
        await this.sync(accountsToSync);
      } catch (error) {
        this.logService.debug(
          `Bootstrap auto-sync (non-Fiskil) failed: ${error instanceof Error ? error.message : JSON.stringify(error)}`,
        );
      }
    });
  }

  /**
   * Fiskil on login: recovery import when backend or vault is behind Fiskil API;
   * otherwise incremental sync for vault Fiskil accounts.
   * @returns true when a recovery import or incremental sync already ran here (caller should not sync Fiskil again).
   */
  async bootstrapFiskilSync(): Promise<boolean> {
    if (!this.pluginConfigService.hasEnabledFiskilPluginConfig()) {
      return false;
    }
    if (!this.userStore.subscription.activeSubscription()?.isPremium) {
      return false;
    }
    if (this.glossSyncService.runningSyncState().isRunning) {
      return false;
    }

    let consents: { consents?: unknown[] };
    try {
      consents = await this.fiskilApi.getActiveConsents();
    } catch (error) {
      this.logService.warning(
        `Fiskil bootstrap skipped: active consents unavailable: ${JSON.stringify(error)}`,
      );
      return false;
    }
    if ((consents.consents ?? []).length === 0) {
      return false;
    }

    const vaultFiskilAccounts = this.userStore.getFiskilAccounts();
    const writableVaultFiskilAccounts = vaultFiskilAccounts.filter(
      (acc) => !isExcludedFromAutoSync(acc),
    );

    let status;
    try {
      status = await this.fiskilApi.getSyncStatus();
    } catch (error) {
      this.logService.warning(
        `Fiskil bootstrap skipped: sync-status unavailable: ${JSON.stringify(error)}`,
      );
      return false;
    }

    let apiAccountIds: string[] | null = null;
    if (!shouldRecoverFiskilImport(status)) {
      apiAccountIds = await this.fiskilApi.listApiAccountIdsWithRetry();
      if (apiAccountIds === null) {
        this.logService.warning(
          "Fiskil bootstrap: could not list API accounts for vault gap check after retry.",
        );
      }
    }

    // Only a syncable account's stalled import should force a recovery — an org-owned account
    // can never get its transactions imported by this user (see isExcludedFromAutoSync), so it
    // would otherwise look permanently "missing" and force an ungated recovery import every
    // time. Vault-vs-API reconciliation below still uses the full (unfiltered) list, since
    // excluding a syncable account here would make it look absent from the vault and trigger
    // recovery for the opposite reason.
    const vaultAccountsMissingTransactions = fiskilVaultAccountsMissingImportedTransactions(
      writableVaultFiskilAccounts,
      this.transactionStore.transaction.transactionViews() ?? [],
    );

    const action = resolveFiskilBootstrapAction(status, apiAccountIds, vaultFiskilAccounts, {
      vaultAccountsMissingTransactions,
    });

    if (action === "recovery") {
      this.logService.info("Bootstrap Fiskil recovery import.");
      await this.importFiskilBootstrapRecovery(status);
      return true;
    }

    if (action === "sync" && writableVaultFiskilAccounts.length > 0) {
      try {
        await this.sync(writableVaultFiskilAccounts);
      } catch (error) {
        this.logService.debug(
          `Bootstrap Fiskil incremental sync failed: ${error instanceof Error ? error.message : JSON.stringify(error)}`,
        );
      }
      // Already synced here, so bootstrapAutoSync must not sync Fiskil accounts again.
      return true;
    }

    return false;
  }

  /** Recovery import on login without user-facing error dialogs. */
  private async importFiskilBootstrapRecovery(
    syncStatus?: FiskilImportData["syncStatus"],
  ): Promise<void> {
    this.glossSyncService.setRefreshBlocked(true);
    this.glossSyncService.runningSyncState.set({
      ...this.glossSyncService.runningSyncState(),
      isRunning: true,
      origin: Origin.fiskil,
    });
    try {
      await this.syncFactory.import(Origin.fiskil, { mode: "recovery", syncStatus });
    } catch (error) {
      await this.glossSyncService.logError(
        [],
        error instanceof Error ? error : new Error(String(error)),
        "Bootstrap Fiskil recovery import",
        SyncStatusEnum.unknownError,
      );
    } finally {
      this.glossSyncService.setRefreshBlocked(false);
      if (this.glossSyncService.runningSyncState().isRunning) {
        this.glossSyncService.initializedRunningSyncState();
      }
    }
  }

  /**
   * Plaid on login (parallel to {@link bootstrapFiskilSync}, never altering it): runs a recovery
   * import only when the first import was interrupted — the server reports linked Items but the vault
   * has no Plaid accounts, or vault accounts are missing imported transactions. Incremental sync of
   * healthy Plaid accounts is left to the generic auto-sync pass in {@link bootstrapAutoSync}
   * (`PlaidSync.sync`), so it is not duplicated here.
   * @returns true when a recovery import ran (caller should not auto-sync Plaid again).
   */
  async bootstrapPlaidSync(): Promise<boolean> {
    return this.runPlaidRecoveryIfNeeded("bootstrap");
  }

  /**
   * Manage-accounts page safety net: Plaid Link completes inline (no redirect handler), so recovery
   * must run when the user opens this page without a fresh login bootstrap.
   */
  async recoverPlaidSessionIfNeeded(): Promise<void> {
    await this.runPlaidRecoveryIfNeeded("session");
  }

  private async runPlaidRecoveryIfNeeded(context: PlaidRecoveryContext): Promise<boolean> {
    if (this.plaidRecoveryInFlight) {
      return this.plaidRecoveryInFlight;
    }

    const run = this.doRunPlaidRecoveryIfNeeded(context);
    this.plaidRecoveryInFlight = run;
    try {
      return await run;
    } finally {
      this.plaidRecoveryInFlight = null;
    }
  }

  private async doRunPlaidRecoveryIfNeeded(context: PlaidRecoveryContext): Promise<boolean> {
    if (!this.userStore.subscription.activeSubscription()?.isPremium) {
      return false;
    }
    if (this.glossSyncService.runningSyncState().isRunning) {
      return false;
    }

    const evaluation = await this.evaluatePlaidRecovery(context);
    if (!evaluation || evaluation.action !== "recovery") {
      return false;
    }

    if (context === "session") {
      const toastKey =
        evaluation.vaultPlaidAccounts.length === 0 && evaluation.serverItemCount > 0
          ? "plaidRecoverMissingAccounts"
          : "plaidRecoverPendingImport";

      this.logService.info("Recovering Plaid import (server items or missing vault accounts).");
      this.globalService.showInfoMessage("info", toastKey);
      await this.import(Origin.plaid, { mode: "recovery" });
    } else {
      this.logService.info("Bootstrap Plaid recovery import.");
      await this.importPlaidBootstrapRecovery();
    }

    return true;
  }

  /**
   * Shared fetch + plugin-config backfill + {@link resolvePlaidBootstrapAction} for login bootstrap
   * and manage-accounts session recovery.
   */
  private async evaluatePlaidRecovery(
    context: PlaidRecoveryContext,
  ): Promise<PlaidRecoveryEvaluation | null> {
    let serverItemCount = 0;
    let hasUnsavedSyncedData = false;
    let accountsSynced = false;
    try {
      const [{ items = [] }, syncStatus] = await Promise.all([
        this.plaidApi.getItems(),
        this.plaidApi.getSyncStatus().catch((): null => null),
      ]);
      serverItemCount = items.length;
      hasUnsavedSyncedData = syncStatus?.has_unsaved_synced_data ?? false;
      accountsSynced = syncStatus?.accounts_synced ?? false;
    } catch (error) {
      const label = context === "bootstrap" ? "bootstrap" : "recovery check";
      this.logService.warning(
        `Plaid ${label} skipped: items unavailable: ${JSON.stringify(error)}`,
      );
      return null;
    }

    if (serverItemCount === 0 && !this.pluginConfigService.hasEnabledPlaidPluginConfig()) {
      return null;
    }

    if (serverItemCount > 0 && !this.pluginConfigService.hasEnabledPlaidPluginConfig()) {
      try {
        await this.pluginConfigService.ensurePlaidPluginActivated();
      } catch (configError) {
        this.logService.warning(`Plaid plugin config backfill failed: ${configError}`);
      }
    }

    const vaultPlaidAccounts = this.userStore.getPlaidAccounts();
    const writableVaultPlaidAccounts = vaultPlaidAccounts.filter(
      (acc) => !isExcludedFromAutoSync(acc),
    );

    // Only a syncable account's stalled import should force a recovery — an org-owned account
    // can never get its transactions imported by this user (see isExcludedFromAutoSync), so it
    // would otherwise look permanently "missing" and force an ungated recovery import every
    // time. Vault-presence check below still uses the full (unfiltered) list, since excluding a
    // syncable account here would make it look absent from the vault and trigger recovery for
    // the opposite reason.
    const vaultAccountsMissingTransactions = plaidVaultAccountsMissingImportedTransactions(
      writableVaultPlaidAccounts,
      this.transactionStore.enrichTransaction.collection(),
    );

    const action = resolvePlaidBootstrapAction(serverItemCount, vaultPlaidAccounts, {
      vaultAccountsMissingTransactions,
      hasUnsavedSyncedData,
      accountsSynced,
    });

    return { action, serverItemCount, vaultPlaidAccounts };
  }

  /** Recovery import on login without user-facing error dialogs. Mirrors importFiskilBootstrapRecovery. */
  private async importPlaidBootstrapRecovery(): Promise<void> {
    this.glossSyncService.setRefreshBlocked(true);
    this.glossSyncService.runningSyncState.set({
      ...this.glossSyncService.runningSyncState(),
      isRunning: true,
      origin: Origin.plaid,
    });
    try {
      await this.syncFactory.import(Origin.plaid, { mode: "recovery" });
    } catch (error) {
      await this.glossSyncService.logError(
        [],
        error instanceof Error ? error : new Error(String(error)),
        "Bootstrap Plaid recovery import",
        SyncStatusEnum.unknownError,
      );
    } finally {
      this.glossSyncService.setRefreshBlocked(false);
      if (this.glossSyncService.runningSyncState().isRunning) {
        this.glossSyncService.initializedRunningSyncState();
      }
    }
  }

  /** set the initial syncing values; `syncableAccounts` is already filtered by sync() */
  private async initializeSync(syncableAccounts: AccountView[]) {
    try {
      const initialStates: SyncStatusView[] = [];
      const autoAccounts: AccountView[] = [];
      for (const acc of syncableAccounts) {
        const account = acc.clone();
        const initialAccountState = await this.initialSyncStatus(account);
        initialStates.push(initialAccountState);
        // Replace rather than append — syncStatusLink is a pointer to the current sync status,
        // not a history (see terminateSync/markAsSyncing). Left as push(), it grows forever and
        // the account's own cipher payload eventually fails backend validation on write
        // (observed: a heavily-synced Fiskil test account hit 1,244 linked entries).
        account.syncStatusLink = [{ id: initialAccountState.id }];
        autoAccounts.push(account);
      }

      await this.importStore.syncStatus.saveToVault(initialStates, true);
      await this.userStore.accounts.saveToVault(autoAccounts, true);

      return autoAccounts;
    } catch (error) {
      await this.glossSyncService.terminateSync(syncableAccounts, SyncStatusEnum.unknownError);
      await this.glossSyncService.logError(
        syncableAccounts,
        error,
        "Initializing Sync",
        SyncStatusEnum.unknownError,
      );
      this.confirmationDialog.notify("error", "error", "somethingWentWrong");
      return [];
    }
  }

  /** generate initial sync state */
  private async initialSyncStatus(account: AccountView) {
    try {
      return new SyncStatusView({
        id: crypto.randomUUID(),
        key: SyncStatusEnum.syncing,
        vid: null,
        dc: new Date().toISOString(),
        dm: new Date().toISOString(),
        v: 2,
        err: null,
        acId: account.id,
      });
    } catch (error) {
      await this.glossSyncService.logError(
        [account],
        error,
        "generating initial sync status",
        SyncStatusEnum.unknownError,
      );
    }
  }

  /** Generate map between accounts and their origins */
  private originAccountsMap(accounts: AccountView[]): OriginAccountsMap {
    try {
      const originAccountsMap: OriginAccountsMap = new Map();
      for (const account of accounts) {
        const origin = account.origin;
        if (!originAccountsMap.has(origin)) {
          originAccountsMap.set(origin, []);
        }

        originAccountsMap.get(origin).push(account);
      }

      return originAccountsMap;
    } catch (error) {
      this.logService.debug(
        `Grouping accounts by origin failed: ${error instanceof Error ? error.message : JSON.stringify(error)}`,
      );
    }
  }

  /** Checks for syncable accounts...manual and org-owned ones are not syncable. */
  private isSyncableAccounts(accounts: AccountView[]): boolean {
    try {
      return accounts.length > 0 && accounts.some((acc) => !isExcludedFromAutoSync(acc));
    } catch (error) {
      this.logService.debug(
        `isSyncableAccounts check failed: ${error instanceof Error ? error.message : JSON.stringify(error)}`,
      );
      return false;
    }
  }
}
